import { Search, LogOut, X, MessageSquare } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { useGetOcurrences } from "@/hooks/useOccurrences";
import { Badge } from "@/components/ui/badge";
import toast from "react-hot-toast";
import { useChatNotifications } from "@/context/ChatNotificationsContext";
import { clearSentryUser } from "@/lib/sentryUser";

export const AppBar = () => {
  const navigate = useNavigate();
  const { totalUnread } = useChatNotifications();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 500);
  const searchRef = useRef<HTMLDivElement>(null);

  const { data: occurrences, isLoading } = useGetOcurrences({
    search: debouncedSearch.length >= 3 ? debouncedSearch : "",
    limit: 5,
  });

  // Fecha o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Abre o dropdown quando há resultados
  useEffect(() => {
    if (debouncedSearch.length >= 3 && occurrences?.data?.length > 0) {
      setIsSearchOpen(true);
    }
  }, [debouncedSearch, occurrences]);

  const handleOccurrenceClick = (protocol: string) => {
    navigate(`/occurrences/${protocol}`);
    setSearchTerm("");
    setIsSearchOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("duas-vidas-token");
    localStorage.removeItem("duas-vidas-user");
    clearSentryUser();
    toast.success("Logout realizado com sucesso!");
    navigate("/");
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      {
        label: string;
        variant: "default" | "warning" | "destructive" | "secondary";
      }
    > = {
      pending: { label: "Pendente", variant: "warning" },
      in_progress: { label: "Em Andamento", variant: "secondary" },
      resolved: { label: "Resolvida", variant: "default" },
      // rejected: { label: "Rejeitada", variant: "destructive" },
      returned: { label: "Devolvida", variant: "warning" },
    };

    const config = statusConfig[status.toLowerCase()] || {
      label: status,
      variant: "secondary" as const,
    };
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  return (
    <header className="sticky top-0 w-full h-14 bg-white z-40 flex items-center px-4 gap-3 shadow-sm shrink-0">
      <div className="flex-1 max-w-2xl relative" ref={searchRef}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Pesquisa uma ocorrência pelo protocolo"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => {
              if (
                debouncedSearch.length >= 3 &&
                occurrences?.data?.length > 0
              ) {
                setIsSearchOpen(true);
              }
            }}
            className="w-full pl-9 pr-9 py-2 bg-gray-100 rounded-lg text-sm placeholder:font-normal placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm("");
                setIsSearchOpen(false);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>

        {/* Dropdown de Sugestões */}
        {isSearchOpen && debouncedSearch.length >= 3 && (
          <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-pulse">Buscando ocorrências...</div>
              </div>
            ) : occurrences?.data?.length > 0 ? (
              <div className="py-2">
                <div className="px-4 py-2 text-xs text-gray-500 font-medium border-b">
                  {occurrences.data.length} resultado
                  {occurrences.data.length > 1 ? "s" : ""} encontrado
                  {occurrences.data.length > 1 ? "s" : ""}
                </div>
                {occurrences.data.map((occurrence: any) => (
                  <button
                    key={occurrence.protocol}
                    onClick={() => handleOccurrenceClick(occurrence.protocol)}
                    className="w-full px-3 py-2 hover:bg-gray-50 transition-colors text-left border-b last:border-b-0 cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">
                            #{occurrence.protocol}
                          </span>
                          {getStatusBadge(occurrence.status)}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-1 mb-1">
                          {occurrence.description}
                        </p>
                        {occurrence.address && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="truncate">
                              {occurrence.address}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                Nenhuma ocorrência encontrada
              </div>
            )}
          </div>
        )}

        {/* Mensagem de ajuda */}
        {searchTerm.length > 0 && searchTerm.length < 3 && (
          <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50">
            <p className="text-sm text-gray-500 text-center">
              Digite pelo menos 3 caracteres para buscar
            </p>
          </div>
        )}
      </div>

      {/* Indicador de mensagens não lidas */}
      <button
        onClick={() => navigate("/occurrences")}
        className="relative p-1.5 hover:bg-[hsl(var(--primary))]/10 rounded-lg transition-colors cursor-pointer group"
        title={totalUnread > 0 ? `${totalUnread} mensagem(ns) não lida(s)` : "Mensagens"}
      >
        <MessageSquare className="h-4 w-4 text-gray-600 group-hover:text-[hsl(var(--primary))] transition-colors" />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      <button
        onClick={handleLogout}
        className="relative p-1.5 hover:bg-[hsl(var(--primary))]/10 rounded-lg transition-colors group cursor-pointer"
        title="Sair do Sistema"
      >
        <LogOut className="h-4 w-4 text-gray-600 group-hover:text-[hsl(var(--primary))] transition-colors cursor-pointer" />
      </button>
    </header>
  );
};
